import { ARTICLE_POLICY_VERSION, CONCERN_LABELS, articlePresentation, hasNoAdverseConduct } from './adverseMediaArticlePolicy';
import { articleConcernRating } from './adverseMediaConcernRating';
import { articleReviewReasons } from './adverseMediaReviewReasons';

const concernPriority = { high: 4, medium: 3, review: 2, low: 1 };
const levelPriority = { high: 3, medium: 2, low: 1 };
const nameLevels = { strong: 'high', moderate: 'medium', weak: 'low' };

function severityLevel(assessment) {
  if (assessment?.assessmentVersion !== ARTICLE_POLICY_VERSION) return 'review';
  const severity = assessment.amlSeverityLabel;
  if (severity === 'none') return 'low';
  if (levelPriority[severity]) return severity;
  if (severity) return 'review';
  // Current structured findings also support assessments saved without the label.
  const levels = (assessment.findings || []).map(f => f.severity === 'none' ? 'low' : f.severity)
    .filter(level => levelPriority[level]);
  if (levels.length) return levels.sort((a, b) => levelPriority[b] - levelPriority[a])[0];
  return assessment.involvement === 'contextual' ? 'low' : 'review';
}

const numeric = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const stableKey = row => `${row.link || ''}\n${row.id || ''}`;

export function highestConcernArticleIndicators(rows = [], assessments = {}) {
  let unavailableCount = 0;
  let pendingCount = 0;
  let uncertainCount = 0;
  const candidates = [];
  const uncertain = [];
  const cleanArticles = [];
  for (const row of rows) {
    const result = assessments[String(row.id)];
    if (!result) { pendingCount += 1; continue; }
    const display = articlePresentation(row, result);
    if (display.concern === 'unavailable') { unavailableCount += 1; continue; }
    // A validated zero name match is unrelated, regardless of reported conduct.
    if (display.concern === 'none') continue;
    const assessment = result.assessment;
    const noAdverseConduct = hasNoAdverseConduct(assessment);
    const matched = assessment?.assessmentVersion === ARTICLE_POLICY_VERSION &&
      Number.isFinite(assessment?.nameAssessment?.score) && assessment.nameAssessment.score > 0 &&
      ['strong', 'moderate'].includes(display.nameStrength);
    const rating = articleConcernRating(assessment, display.concern);
    const qualifies = matched && rating !== null;
    const reviewReasons = articleReviewReasons(assessment, display.concern);
    // An allegation requiring review is not itself an uncertain assessment.
    if (!noAdverseConduct && (!qualifies || display.concern === 'review')) uncertainCount += 1;
    (noAdverseConduct ? cleanArticles : qualifies ? candidates : uncertain).push({
      row,
      // Uncertain matches are a review fallback, even when their article is contextual.
      concern: noAdverseConduct ? 'low' : qualifies ? rating : 'review',
      reviewRequired: reviewReasons.length > 0,
      reviewReasons,
      severity: severityLevel(assessment),
      nameMatch: nameLevels[display.nameStrength] || (noAdverseConduct ? 'unavailable' : 'review'),
      sourceQuality: levelPriority[display.sourceQuality] ? display.sourceQuality : 'review',
      implicated: noAdverseConduct ? false : assessment?.assessmentVersion !== ARTICLE_POLICY_VERSION ? null
        : assessment.involvement === 'direct' ? true
        : assessment.involvement === 'contextual' ? false : null,
      nameScore: numeric(assessment?.nameAssessment?.score),
      sourceScore: numeric(row.sourceQualityScore),
    });
  }
  const compareArticles = (a, b) =>
    concernPriority[b.concern] - concernPriority[a.concern] ||
    (levelPriority[b.severity] || 0) - (levelPriority[a.severity] || 0) ||
    (levelPriority[b.nameMatch] || 0) - (levelPriority[a.nameMatch] || 0) ||
    b.nameScore - a.nameScore ||
    (levelPriority[b.sourceQuality] || 0) - (levelPriority[a.sourceQuality] || 0) ||
    b.sourceScore - a.sourceScore ||
    (stableKey(a.row) < stableKey(b.row) ? -1 : stableKey(a.row) > stableKey(b.row) ? 1 : 0);
  candidates.sort(compareArticles);
  uncertain.sort(compareArticles);
  cleanArticles.sort(compareArticles);
  // Assessment gaps do not displace a usable, well-matched finding.
  const article = candidates[0] || uncertain[0] || cleanArticles[0] || null;
  const concern = article?.concern || (unavailableCount || pendingCount ? 'unavailable' : 'none');
  return {
    article,
    concern,
    concernLabel: concern === 'none' ? 'No relevant hits' : CONCERN_LABELS[concern],
    unavailableCount,
    pendingCount,
    uncertainCount,
    incomplete: unavailableCount + pendingCount > 0,
  };
}

export function categoryGauge(level) {
  const rank = levelPriority[level];
  return rank ? { label: level[0].toUpperCase() + level.slice(1), rank, fill: rank / 3 * 100 }
    : { label: level === 'none' ? 'No relevant hits' : level === 'unavailable' ? 'Unavailable' : 'Review needed', rank: null, fill: 0 };
}
